/* ===========================================================
   Lucraa — Regra de atribuição last-touch (decisions.md §9 + §18)
   Escrita por extenso, com teste: descobri-la no código é "como
   nascem números que ninguém sabe explicar" (ADR §9). A partir dos
   sinais capturados no pedido, resolve QUAL canal recebe o crédito
   e POR QUÊ — a evidência que torna cada pedido auditável e explica
   a divergência roasPlat × roasReal (§18 r5).
   Pura: sem React, sem formatação (datas/% são da tela).
   =========================================================== */

// Os TIPOS de atribuição fazem parte do contrato (PedidoAuditavel) e vivem no
// shared (M1.1); a LÓGICA (cascata + regex de medium pago) continua aqui.
// Re-export para os consumidores do domínio não mudarem de import.
import type { CanalAtribuido, DegrauAtribuicao, SinaisAtribuicao } from "../shared";

export type { CanalAtribuido, DegrauAtribuicao, SinaisAtribuicao };

/** Regex oficial do Paid Social no GA4 (decisions.md §18 r3 —
 *  support.google.com/analytics/answer/9756891). Sem ela, um post orgânico do
 *  Instagram (utm_source=instagram) inflaria o ROAS real da Meta. */
export const MEDIUM_PAGO = /^(.*cp.*|ppc|retargeting|paid.*)$/i;

/** Mapa fixo utm_source/channelHint → canal de mídia (decisions.md §9.1). */
const FONTE_MIDIA: Record<string, "meta" | "gads"> = {
  meta: "meta",
  facebook: "meta",
  instagram: "meta",
  ig: "meta",
  google: "gads",
  adwords: "gads",
};

const CANAL_LABEL: Record<CanalAtribuido["canalId"], string> = {
  meta: "Meta Ads",
  gads: "Google Ads",
  social: "Social orgânico",
  organico: "Orgânico",
  email: "E-mail",
  direto: "Direto",
};

function norm(s: string | null): string | null {
  if (s == null) return null;
  const t = s.trim().toLowerCase();
  return t === "" ? null : t;
}

function canal(
  canalId: CanalAtribuido["canalId"],
  paga: boolean,
  degrau: DegrauAtribuicao,
): CanalAtribuido {
  return { canalId, canal: CANAL_LABEL[canalId], paga, degrau };
}

/**
 * Resolve o canal de crédito de um pedido pela cascata last-touch do ADR §9,
 * refinada pelo filtro de medium pago do ADR §18 r3. Para no primeiro degrau
 * que casa. O join campanha (utm_id ↔ AdCampaign) é separado — vive na leitura.
 */
export function resolverAtribuicao(sinais: SinaisAtribuicao): CanalAtribuido {
  const source = norm(sinais.utmSource);
  const medium = norm(sinais.utmMedium);

  // §9.1 — utm_source presente é o degrau de maior precedência.
  if (source) {
    const midia = FONTE_MIDIA[source];
    // §18 r3 — utm_source de mídia só CREDITA pago se o medium for pago; senão
    // é social orgânico (o erro que o produto existe para denunciar).
    if (midia) {
      if (medium && MEDIUM_PAGO.test(medium)) return canal(midia, true, "utm_pago");
      // medium não-pago: Meta/IG/FB vira social orgânico; Google sem medium pago
      // é orgânico de busca (não há "social do Google").
      return canal(midia === "meta" ? "social" : "organico", false, "utm_organico");
    }
    // utm_source não-mídia: e-mail (newsletter/medium=email) ou orgânico genérico (§9.1).
    if (medium === "email" || source === "email" || source === "newsletter")
      return canal("email", false, "utm_organico");
    return canal("organico", false, "utm_organico");
  }

  // §9.2 — canal informado pela plataforma. Sem medium pago no sinal, não
  // credita mídia paga (humano não recupera sinal que o clique não carregou, §18 r4).
  const hint = norm(sinais.channelHint);
  if (hint) {
    const midia = FONTE_MIDIA[hint];
    if (midia) return canal(midia === "meta" ? "social" : "organico", false, "channel_hint");
    if (hint === "email" || hint === "newsletter") return canal("email", false, "channel_hint");
    if (hint === "direct" || hint === "direto") return canal("direto", false, "channel_hint");
    return canal("organico", false, "channel_hint");
  }

  // §9.4 — sem sinal nenhum (dark traffic → (direct)/(none)).
  return canal("direto", false, "direto");
}
